import Modal from './Modal';
import DescriptionListItem from './DescriptionListItem';

function FundingTechnicalDetailsModal({ item, open, setOpen }) {
  return (
    <Modal open={open} setOpen={setOpen} bodyClass="px-6 sm:!px-0 sm:!max-w-4xl sm:!mx-auto">
      <div className="px-4 py-6 sm:px-6">
        <h3 className="text-base font-semibold leading-7 text-gray-900">🔧 Technische Bewertungsdetails: {item.title}</h3>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">Detaillierte Scoring-Informationen und Berechnungsgrundlagen</p>
      </div>
      <div className="border-t border-gray-100">
        <dl className="divide-y divide-gray-100">
          
          {/* Final Scoring Results */}
          <div className="border-t-2 border-blue-300 pt-4">
            <h4 className="text-sm font-semibold text-blue-700 mb-2 px-4">📊 Finale Bewertungsergebnisse</h4>
          </div>
          
          <DescriptionListItem
            term="Note (Grade)"
            definition={
              <div className="font-mono text-sm">
                <div className="font-bold text-green-600 text-lg">{item.grade}</div>
                <div className="text-xs text-gray-500">
                  Schulnote 1-6 (kleiner = besser)
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Berechnung: Summe aller absoluten Abweichungen → Note
                </div>
              </div>
            }
          />

          <DescriptionListItem
            term="Summe Absolute Abweichungen"
            definition={
              <div className="font-mono text-sm">
                <div className="font-bold">{item.sumAbsoluteDiff?.toFixed(4)}</div>
                <div className="text-xs text-gray-500">
                  KS1 + KS2 + KS3 + KS4 + DU-Wert
                </div>
              </div>
            }
          />

          {/* Tiebreaker Scores */}
          <div className="border-t-2 border-orange-300 pt-4">
            <h4 className="text-sm font-semibold text-orange-700 mb-2 px-4">🎯 Sortier-Kriterien (bei gleicher Note)</h4>
          </div>

          <DescriptionListItem
            term="Sortier-Scores"
            definition={
              <div className="font-mono text-sm space-y-2">
                <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
                  <span className="font-medium">Hauptscore (DI):</span>
                  <span className="font-bold text-blue-600">{item.hauptscore}</span>
                </div>
                <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
                  <span className="font-medium">Nebenscore (DJ):</span>
                  <span className="font-bold text-blue-600">{item.nebenscore}</span>
                </div>
                <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
                  <span className="font-medium">Publikationsjahr:</span>
                  <span className="font-bold text-blue-600">{item.year}</span>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Bei gleicher Note: höherer Hauptscore → höherer Nebenscore → neueres Jahr
                </div>
              </div>
            }
          />
          
          {/* Absolute Deviations */}
          {item.absoluteDiff && (
            <>
              <div className="border-t-2 border-purple-300 pt-4">
                <h4 className="text-sm font-semibold text-purple-700 mb-2 px-4">📏 Absolute Abweichungen</h4>
              </div>
              
              <DescriptionListItem 
                term="Abweichungen nach Kompetenzstufe" 
                definition={
                  <div className="font-mono text-sm space-y-2">
                    {Object.entries(item.absoluteDiff).map(([level, diff]) => (
                      <div key={level} className="flex justify-between items-center bg-purple-50 p-2 rounded">
                        <span className="font-medium">Kompetenzstufe {level}:</span>
                        <span className="font-bold text-purple-600">
                          {typeof diff === 'number' ? diff.toFixed(6) : diff}
                        </span>
                      </div>
                    ))}
                    <div className="text-xs text-gray-500 mt-2">
                      Abweichung zwischen Förderprofil des Kindes und Förderschwerpunkt des Programms
                      <br />
                      (kleiner = bessere Passung)
                    </div>
                  </div>
                } 
              />
            </>
          )}
          
          {/* Profile Data */}
          {item.profiles && (
            <>
              <div className="border-t-2 border-green-300 pt-4">
                <h4 className="text-sm font-semibold text-green-700 mb-2 px-4">👤 Förderprofil-Daten des Kindes</h4>
              </div>
              
              <DescriptionListItem 
                term="Berechnete Profile" 
                definition={
                  <div className="font-mono text-xs space-y-3">
                    {Object.entries(item.profiles).map(([category, levels]) => (
                      <div key={category} className="border border-green-200 p-3 rounded bg-green-50">
                        <div className="font-semibold text-green-800 mb-2">
                          Kategorie {category}:
                        </div>
                        {Object.entries(levels).map(([level, data]) => (
                          <div key={level} className="ml-2 mb-2 p-2 bg-white rounded border">
                            <div className="font-medium text-green-700">Kompetenzstufe {level}:</div>
                            <div className="ml-2 space-y-1 text-xs">
                              {data.recodedPR !== undefined && (
                                <div>
                                  <strong>Recoded PR:</strong> {data.recodedPR}
                                  <span className="text-gray-500 ml-2">(Umkodierte Prozentrang-Werte)</span>
                                </div>
                              )}
                              {data.weightedPR !== undefined && data.weightedPR !== -99 && (
                                <div>
                                  <strong>Weighted PR:</strong> {data.weightedPR.toFixed(4)}
                                  <span className="text-gray-500 ml-2">(Gewichtete Prozentrang-Werte)</span>
                                </div>
                              )}
                              {data.profileScore !== undefined && data.profileScore !== -99 && (
                                <div>
                                  <strong>Profile Score:</strong> {data.profileScore.toFixed(4)}
                                  <span className="text-gray-500 ml-2">(Finaler Profil-Score für diese KS)</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                    <div className="text-xs text-gray-500 mt-3 p-2 bg-gray-100 rounded">
                      <strong>Erklärung:</strong> Diese Daten zeigen das berechnete Förderprofil des Kindes basierend auf den eingegebenen Testergebnissen. 
                      Die Profile Scores werden verwendet, um die Passung zu Förderprogrammen zu berechnen.
                    </div>
                  </div>
                } 
              />
            </>
          )}
          
          {/* Program Information */}
          <div className="border-t-2 border-gray-300 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 px-4">ℹ️ Programm-Informationen</h4>
          </div>
          
          <DescriptionListItem 
            term="Grundlegende Daten" 
            definition={
              <div className="font-mono text-sm space-y-1">
                <div><strong>Kurz-Name:</strong> {item.shortName}</div>
                <div><strong>Autoren:</strong> {item.authors?.join(', ')}</div>
                <div><strong>Jahr:</strong> {item.year}</div>
                <div><strong>Verlag:</strong> {item.publisher}</div>
                <div><strong>Zielklassen:</strong> {item.classes ? item.classes.join(', ') : 'Nicht spezifiziert'}</div>
              </div>
            } 
          />
          
          {/* Raw Data for Debugging */}
          <div className="border-t-2 border-red-300 pt-4">
            <h4 className="text-sm font-semibold text-red-700 mb-2 px-4">🐛 Debug-Informationen</h4>
          </div>
          
          <DescriptionListItem 
            term="Rohdaten-Export" 
            definition={
              <details className="font-mono text-xs">
                <summary className="cursor-pointer text-blue-600 hover:text-blue-800 font-semibold">
                  Vollständige technische Daten anzeigen
                </summary>
                <div className="bg-gray-100 p-3 rounded mt-2 max-h-96 overflow-y-auto">
                  <pre>{JSON.stringify({
                    grade: item.grade,
                    sumAbsoluteDiff: item.sumAbsoluteDiff,
                    hauptscore: item.hauptscore,
                    nebenscore: item.nebenscore,
                    absoluteDiff: item.absoluteDiff,
                    profiles: item.profiles,
                    testItem: item.testItem
                  }, null, 2)}</pre>
                </div>
              </details>
            } 
          />
          
        </dl>
      </div>
    </Modal>
  );
}

export default FundingTechnicalDetailsModal;